import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Plus, FileBox, ChevronRight, User as UserIcon } from 'lucide-react';
import { useLang } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const statusColor = (s) => ({
  quote_requested: 'bg-amber-50 text-amber-700 border-amber-200',
  quoted:          'bg-blue-50 text-blue-700 border-blue-200',
  approved:        'bg-emerald-50 text-emerald-700 border-emerald-200',
  in_production:   'bg-orange-50 text-orange-700 border-orange-200',
  shipped:         'bg-indigo-50 text-indigo-700 border-indigo-200',
  completed:       'bg-zinc-100 text-zinc-700 border-zinc-200',
  cancelled:       'bg-rose-50 text-rose-700 border-rose-200'
}[s] || 'bg-zinc-100 text-zinc-700 border-zinc-200');

const Dashboard = () => {
  const { t, lang } = useLang();
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/orders/mine`);
        setOrders(data);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div className="bg-white min-h-screen">
      <div className="max-w-7xl mx-auto px-5 lg:px-8 py-10">
        <div className="flex items-center justify-between flex-wrap gap-4 mb-8">
          <div>
            <div className="flex items-center gap-2 text-zinc-500 text-sm">
              <UserIcon className="w-4 h-4" /> {t.auth.welcome}, <span className="text-zinc-900 font-medium">{user?.name}</span>
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-zinc-900 mt-2">{t.dashboard.title}</h1>
            <p className="text-zinc-600 mt-1">{t.dashboard.subtitle}</p>
          </div>
          <Link to="/quote">
            <Button className="bg-orange-500 hover:bg-orange-600 text-white border-0">
              <Plus className="w-4 h-4 mr-2" /> {t.dashboard.newQuote}
            </Button>
          </Link>
        </div>

        {loading ? (
          <div className="text-zinc-500 text-sm">…</div>
        ) : orders.length === 0 ? (
          <Card className="bg-white border-zinc-200 p-10 text-center">
            <FileBox className="w-10 h-10 text-zinc-300 mx-auto mb-3" />
            <p className="text-zinc-500">{t.dashboard.empty}</p>
            <Link to="/quote" className="inline-block mt-4">
              <Button className="bg-orange-500 hover:bg-orange-600 text-white border-0">
                <Plus className="w-4 h-4 mr-2" /> {t.dashboard.newQuote}
              </Button>
            </Link>
          </Card>
        ) : (
          <div className="space-y-3">
            {orders.map((o) => (
              <Link
                key={o.id}
                to={`/dashboard/orders/${o.order_number}`}
                className="block group"
              >
                <Card className="bg-white border-zinc-200 hover:border-orange-400 hover:shadow-md transition-all p-5">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-lg bg-orange-500/10 border border-orange-500/20 flex items-center justify-center shrink-0">
                      <FileBox className="w-5 h-5 text-orange-500" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-zinc-900 font-semibold">{o.order_number}</span>
                        <Badge className={`${statusColor(o.status)} border font-medium`}>
                          {t.dashboard.statuses[o.status] || o.status}
                        </Badge>
                      </div>
                      <div className="text-xs text-zinc-500 mt-1">
                        {new Date(o.created_at).toLocaleString(lang === 'tr' ? 'tr-TR' : 'en-US')}
                        <span className="mx-1.5">·</span>
                        {(o.technology || '').toUpperCase()} · {o.material_label} · {o.color} · {t.dashboard.qty}: {o.quantity}
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      {o.quoted_price ? (
                        <div className="text-orange-500 font-bold text-lg">{o.quoted_price.toLocaleString('tr-TR')} ₺</div>
                      ) : (
                        <div className="text-zinc-400 text-xs italic">{t.dashboard.pending}</div>
                      )}
                    </div>
                    <ChevronRight className="w-5 h-5 text-zinc-400 group-hover:text-orange-500 transition-colors" />
                  </div>
                </Card>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
